import React, { useState, useEffect } from 'react';
import { RefreshCw, ArrowUpRight, ArrowDownRight, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


const TIER_ORDER = { paid: 1, premium: 2, share: 3 };
const TIER_LABELS = { paid: 'Standard', premium: 'Premium', share: 'Share' };
const TIER_COLORS = { paid: '#3b82f6', premium: '#f59e0b', share: '#10b981' };

export default function AdminPlanChangesPage() {
  const { isDark, colors } = useAdminTheme();
  const [data, setData] = useState({ changes: [], total: 0, page: 1, pages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    axios.get(`${API}/admin/plan-changes/stats`, { withCredentials: true })
      .then(res => setStats(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/plan-changes`, {
      params: { page, limit: 15 },
      withCredentials: true,
    })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page]);

  const cardStyle = { background: colors.card, border: `1px solid ${colors.cardBorder}` };
  const rowStyle = { background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-plan-changes-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: colors.text }}>
              <RefreshCw className="h-6 w-6" style={{ color: '#3b82f6' }} />
              Plan Changes
            </h1>
            <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
              Track all subscription upgrades and downgrades
            </p>
          </div>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="plan-change-stats">
            {[
              { label: 'Total Changes', value: stats.total, color: '#3b82f6' },
              { label: 'Upgrades', value: stats.upgrades, color: '#22c55e' },
              { label: 'Downgrades', value: stats.downgrades, color: '#f59e0b' },
              { label: 'Last 30 Days', value: stats.last_30_days, color: '#8b5cf6' },
            ].map(s => (
              <div key={s.label} className="rounded-xl p-4" style={cardStyle}>
                <p className="text-xs font-medium uppercase tracking-wide" style={{ color: colors.textSecondary }}>{s.label}</p>
                <p className="text-2xl font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Flow breakdown */}
        {stats?.flows && Object.keys(stats.flows).length > 0 && (
          <div className="rounded-xl p-4" style={cardStyle} data-testid="plan-change-flows">
            <h3 className="text-sm font-semibold mb-3" style={{ color: colors.text }}>Change Flows</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.flows).map(([flow, count]) => {
                const [from, to] = flow.split(' -> ');
                const isUpgrade = (TIER_ORDER[to] || 0) > (TIER_ORDER[from] || 0);
                return (
                  <div
                    key={flow}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={rowStyle}
                  >
                    <span style={{ color: TIER_COLORS[from] || colors.text }}>{TIER_LABELS[from] || from}</span>
                    {isUpgrade ? (
                      <ArrowUpRight className="h-3 w-3 text-green-500" />
                    ) : (
                      <ArrowDownRight className="h-3 w-3 text-amber-500" />
                    )}
                    <span style={{ color: TIER_COLORS[to] || colors.text }}>{TIER_LABELS[to] || to}</span>
                    <span className="ml-1 px-1.5 py-0.5 rounded-md text-[10px]" style={{ background: isDark ? '#1e293b' : '#e2e8f0', color: colors.textSecondary }}>
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Change history table */}
        <div className="rounded-xl overflow-hidden" style={cardStyle} data-testid="plan-changes-table">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.textSecondary }} />
            </div>
          ) : data.changes.length === 0 ? (
            <div className="text-center py-12">
              <RefreshCw className="h-10 w-10 mx-auto mb-3 opacity-30" style={{ color: colors.textSecondary }} />
              <p className="text-sm" style={{ color: colors.textSecondary }}>No plan changes yet</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                      {['User', 'From', '', 'To', 'Date'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide" style={{ color: colors.textSecondary }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.changes.map((c, i) => {
                      const isUpgrade = (TIER_ORDER[c.to_tier] || 0) > (TIER_ORDER[c.from_tier] || 0);
                      return (
                        <tr
                          key={i}
                          style={{ borderBottom: `1px solid ${colors.cardBorder}` }}
                          data-testid={`plan-change-row-${i}`}
                        >
                          <td className="px-4 py-3">
                            <p className="font-medium" style={{ color: colors.text }}>{c.name || 'Unknown'}</p>
                            <p className="text-xs" style={{ color: colors.textSecondary }}>{c.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="px-2 py-1 rounded-md text-xs font-semibold"
                              style={{ background: `${TIER_COLORS[c.from_tier] || '#666'}22`, color: TIER_COLORS[c.from_tier] || colors.text }}
                            >
                              {TIER_LABELS[c.from_tier] || c.from_tier}
                            </span>
                          </td>
                          <td className="px-2 py-3">
                            {isUpgrade ? (
                              <ArrowUpRight className="h-4 w-4 text-green-500" />
                            ) : (
                              <ArrowDownRight className="h-4 w-4 text-amber-500" />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="px-2 py-1 rounded-md text-xs font-semibold"
                              style={{ background: `${TIER_COLORS[c.to_tier] || '#666'}22`, color: TIER_COLORS[c.to_tier] || colors.text }}
                            >
                              {TIER_LABELS[c.to_tier] || c.to_tier}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs" style={{ color: colors.textSecondary }}>
                            {c.changed_at ? new Date(c.changed_at).toLocaleString() : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data.pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
                  <p className="text-xs" style={{ color: colors.textSecondary }}>
                    Page {data.page} of {data.pages} ({data.total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="plan-changes-prev">
                      <ArrowLeft className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} data-testid="plan-changes-next">
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
